/**
 * @fileoverview Configuration centralisée du système de tiers EBEA
 *
 * Ce fichier définit la hiérarchie des tiers, leurs métadonnées,
 * et la matrice d'accès aux features selon le niveau d'abonnement.
 *
 * @module config/tierConfig
 * @author James (Dev) - Story 1.1
 * @version 1.0.0
 */

// =============================================================================
// TIER HIERARCHY
// =============================================================================

/**
 * Hiérarchie des tiers EBEA, du plus bas au plus haut niveau.
 * L'index dans le tableau correspond au niveau d'accès (0 = plus bas).
 *
 * @constant {string[]}
 * @example
 * // Vérifier si un tier est supérieur à un autre
 * const tierAIndex = TIER_HIERARCHY.indexOf('progress');
 * const tierBIndex = TIER_HIERARCHY.indexOf('discovery');
 * const isHigher = tierAIndex > tierBIndex; // true
 */
export const TIER_HIERARCHY = [
  'discovery',
  'essential',
  'progress',
  'fullcare',
  'research_partner'
];

// =============================================================================
// TIER METADATA
// =============================================================================

/**
 * Métadonnées pour chaque tier (label, icône, couleur, cible d'upgrade).
 *
 * @constant {Object.<string, {label: string, icon: string, color: string, upgradeTarget: string|null}>}
 * @property {string} label - Nom d'affichage du tier
 * @property {string} icon - Emoji représentant le tier
 * @property {string} color - Couleur hex du tier (cohérent avec branding AKONGO)
 * @property {string|null} upgradeTarget - Tier suivant pour l'upgrade, null si tier max
 */
export const TIER_META = {
  discovery: {
    label: 'Discovery',
    icon: '🔍',
    color: '#6c757d',
    upgradeTarget: 'essential'
  },
  essential: {
    label: 'EBEA Essentiel',
    icon: '📈',
    color: '#28a745',
    upgradeTarget: 'progress'
  },
  progress: {
    label: 'EBEA Progress',
    icon: '🚀',
    color: '#007bff',
    upgradeTarget: 'fullcare'
  },
  fullcare: {
    label: 'EBEA FullCare',
    icon: '💎',
    color: '#6f42c1',
    upgradeTarget: 'research_partner'
  },
  research_partner: {
    label: 'EBEA Research Partner',
    icon: '🔬',
    color: '#fd7e14',
    upgradeTarget: null
  }
};

// =============================================================================
// FEATURE ACCESS MATRIX
// =============================================================================

/**
 * Matrice d'accès aux features selon le tier minimum requis.
 *
 * Chaque feature définit:
 * - minTier: tier minimum requis pour accéder à la feature
 * - category: catégorie fonctionnelle de la feature
 * - dummyGenerator: (optionnel) nom du générateur de données dummy
 *
 * @constant {Object.<string, {minTier: string, category: string, dummyGenerator?: string}>}
 * @example
 * // Vérifier l'accès à une feature
 * const feature = FEATURE_ACCESS['enrichment.analysis'];
 * const userTierIndex = TIER_HIERARCHY.indexOf(currentTier);
 * const requiredIndex = TIER_HIERARCHY.indexOf(feature.minTier);
 * const hasAccess = userTierIndex >= requiredIndex;
 */
export const FEATURE_ACCESS = {
  // === ENRICHMENT (FullCare) ===
  'enrichment.analysis': {
    minTier: 'fullcare',
    category: 'enrichment',
    dummyGenerator: 'enrichmentGenerator'
  },

  // === MONITORING (Progress) ===
  'environmentalMonitoring': {
    minTier: 'progress',
    category: 'monitoring',
    dummyGenerator: 'environmentalGenerator'
  },

  // === OBSERVATION (Progress) ===
  'scanSampling': {
    minTier: 'progress',
    category: 'observation',
    dummyGenerator: 'campaignGenerator'
  },

  // === HEALTH (FullCare) ===
  'veterinary': {
    minTier: 'fullcare',
    category: 'health',
    dummyGenerator: null // Pas de dummy - données sensibles
  },

  // === AI (FullCare) ===
  'rafikiAI': {
    minTier: 'fullcare',
    category: 'ai',
    dummyGenerator: null // Feature pas encore live
  },

  // === NOTEBOOK (FullCare) ===
  'sectorNotebook.advanced': {
    minTier: 'fullcare',
    category: 'notebook',
    dummyGenerator: null
  },

  // === AUTOMATION (FullCare) ===
  'taskAutomation': {
    minTier: 'fullcare',
    category: 'automation',
    dummyGenerator: null
  },

  // === CAMPAIGNS ===
  'campaign.scanSampling': {
    minTier: 'progress',
    category: 'campaign',
    dummyGenerator: 'campaignGenerator'
  },
  'campaign.environmental': {
    minTier: 'progress',
    category: 'campaign',
    dummyGenerator: 'campaignGenerator'
  },
  'campaign.focalSampling': {
    minTier: 'essential',
    category: 'campaign',
    dummyGenerator: 'campaignGenerator'
  },

  // === VISION (Research Partner) ===
  'akongoVision': {
    minTier: 'research_partner',
    category: 'vision',
    dummyGenerator: null
  }
};

// =============================================================================
// DEFAULT ACCESS
// =============================================================================

/**
 * Configuration par défaut pour les features non listées dans FEATURE_ACCESS.
 * Par défaut, les features non définies sont accessibles à tous (discovery).
 *
 * @constant {{minTier: string, category: string, dummyGenerator: null}}
 */
export const DEFAULT_ACCESS = {
  minTier: 'discovery',
  category: 'default',
  dummyGenerator: null
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Récupère l'index d'un tier dans la hiérarchie.
 *
 * @param {string} tier - Clé du tier
 * @returns {number} Index du tier (-1 si non trouvé)
 */
export const getTierIndex = (tier) => {
  return TIER_HIERARCHY.indexOf(tier);
};

/**
 * Vérifie si un tier a accès à un autre tier (est égal ou supérieur).
 *
 * @param {string} currentTier - Tier actuel de l'utilisateur
 * @param {string} requiredTier - Tier minimum requis
 * @returns {boolean} True si l'accès est autorisé
 */
export const hasTierAccess = (currentTier, requiredTier) => {
  const currentIndex = getTierIndex(currentTier);
  const requiredIndex = getTierIndex(requiredTier);

  if (currentIndex === -1 || requiredIndex === -1) {
    return false;
  }

  return currentIndex >= requiredIndex;
};

/**
 * Récupère la configuration d'une feature.
 * Retourne DEFAULT_ACCESS si la feature n'est pas définie.
 *
 * @param {string} featureKey - Clé de la feature
 * @returns {{minTier: string, category: string, dummyGenerator: string|null}}
 */
export const getFeatureConfig = (featureKey) => {
  return FEATURE_ACCESS[featureKey] || DEFAULT_ACCESS;
};

/**
 * Récupère les métadonnées d'un tier.
 *
 * @param {string} tier - Clé du tier
 * @returns {{label: string, icon: string, color: string, upgradeTarget: string|null}|null}
 */
export const getTierMeta = (tier) => {
  return TIER_META[tier] || null;
};

/**
 * Liste toutes les features accessibles pour un tier donné.
 *
 * @param {string} tier - Tier de l'utilisateur
 * @returns {string[]} Liste des clés de features accessibles
 */
export const getAccessibleFeatures = (tier) => {
  const tierIndex = getTierIndex(tier);

  if (tierIndex === -1) {
    return [];
  }

  return Object.entries(FEATURE_ACCESS)
    .filter(([, config]) => getTierIndex(config.minTier) <= tierIndex)
    .map(([key]) => key);
};

/**
 * Liste les features qui seraient débloquées en passant au tier supérieur.
 *
 * @param {string} currentTier - Tier actuel
 * @returns {{key: string, config: Object}[]} Features du prochain tier
 */
export const getNextTierFeatures = (currentTier) => {
  const tierMeta = getTierMeta(currentTier);

  if (!tierMeta || !tierMeta.upgradeTarget) {
    return [];
  }

  const nextTier = tierMeta.upgradeTarget;
  const nextTierIndex = getTierIndex(nextTier);

  return Object.entries(FEATURE_ACCESS)
    .filter(([, config]) => getTierIndex(config.minTier) === nextTierIndex)
    .map(([key, config]) => ({ key, ...config }));
};
